import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, UtensilsCrossed, Sparkles, Footprints, Pill, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { trackCTAClick } from "@/hooks/usePageTracking";
import { SectionTracker } from "./SectionTracker";

const SERVICE_IMAGES = [
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80",
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80",
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80",
  "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=600&q=80",
  "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=600&q=80",
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80",
];

const Services = () => {
  const { t } = useLanguage();
  const { cms } = useCMS();

  const services = [
    { icon: ShoppingBag, titleKey: "service_errands", descKey: "service_errands_desc", imgAlt: "Caregiver helping with grocery shopping" },
    { icon: UtensilsCrossed, titleKey: "service_cooking", descKey: "service_cooking_desc", imgAlt: "Home-cooked meal preparation" },
    { icon: Sparkles, titleKey: "service_cleaning", descKey: "service_cleaning_desc", imgAlt: "Professional home cleaning and tidying" },
    { icon: Footprints, titleKey: "service_walks", descKey: "service_walks_desc", imgAlt: "Caregiver accompanying elderly person on a walk" },
    { icon: Pill, titleKey: "service_pharmacy", descKey: "service_pharmacy_desc", imgAlt: "Pharmacy pickup and medication management" },
    { icon: Users, titleKey: "service_social", descKey: "service_social_desc", imgAlt: "Social companionship and engaging activities" },
  ];

  return (
    <SectionTracker id="services">
      <section id="services" className="py-20 md:py-28 bg-secondary/50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">{t("services_label")}</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-3 mb-4">
              {t("services_title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("services_subtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const imgKey = `service_${i + 1}` as keyof typeof cms.images;
              const imgSrc = cms.images[imgKey] || SERVICE_IMAGES[i];
              return (
                <motion.div
                  key={service.titleKey}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-card rounded-xl overflow-hidden shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-card)] transition-all duration-300 group flex flex-col card-hover-lift"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={service.imgAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <service.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-display text-xl text-foreground mb-2">{t(service.titleKey)}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-1">{t(service.descKey)}</p>
                    <Button
                      variant="heroPrimary"
                      size="sm"
                      className="w-full mt-auto"
                      asChild
                    >
                      <Link
                        to="/order"
                        onClick={() => trackCTAClick("service_book", { service: service.titleKey })}
                        aria-label={`${t("service_book_cta")} — ${t(service.titleKey)}`}
                      >
                        {t("service_book_cta")}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </SectionTracker>
  );
};

export default Services;
